import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import gsap from 'gsap';
import { Button, SectionHeader, DataState, SkeletonGrid } from '../components/ui';
import { getLocalizedField, formatPrice } from '../lib/format';
import { useReveal } from '../hooks/useReveal';
import cateringLogo from '../assests/ampera_catering_logo.png';

const CATERING_GRID = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';

// Perk icons
const PERK_ICONS = {
  breakfast: '🍳',
  setup: '🛠️',
  delivery: '🚚',
  tableware: '🍽️',
  staff: '👨‍🍳',
  decoration: '🎊',
  drinks: '🥤',
  dessert: '🍰',
};

// Feature Card with scroll reveal
function AnimatedFeatureCard({ icon, title, description, index }) {
  const cardRef = useReveal({ index, stagger: 0.15 });

  return (
    <div ref={cardRef} className="text-center">
      <div className="w-16 h-16 bg-white rounded-xl shadow-sm flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">{icon}</span>
      </div>
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );
}

// Package Card Component
function PackageCard({ pkg, lang, onInquire, t, index = 0 }) {
  const cardRef = useReveal({ index, stagger: 0.1 });
  const name = getLocalizedField(pkg, 'name', lang);
  const description = getLocalizedField(pkg, 'description', lang);
  const menuItems = pkg.menu_items || [];
  const perks = pkg.perks || [];

  return (
    <div ref={cardRef} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col">
      {/* Image */}
      <div className="aspect-[16/9] bg-gray-100 relative overflow-hidden">
        {pkg.image_url ? (
          <img
            src={pkg.image_url}
            alt={name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
            <span className="text-6xl">🍽️</span>
          </div>
        )}

        {/* Featured badge */}
        {pkg.is_featured && (
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 bg-yellow-500 text-white text-sm font-medium rounded-full shadow">
              ⭐ {t('catering.featured')}
            </span>
          </div>
        )}

        {/* Capacity badge */}
        <div className="absolute bottom-3 right-3">
          <span className="px-3 py-1.5 bg-black/70 text-white text-sm font-medium rounded-lg backdrop-blur-sm">
            {pkg.capacity_min}-{pkg.capacity_max} {t('catering.pax')}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-bold text-xl text-gray-900 mb-2">{name}</h3>
        {description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">{description}</p>
        )}

        {/* Price Info */}
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-2xl font-bold text-primary-600">
            {formatPrice(pkg.price_per_pax, pkg.currency) || '-'}
          </span>
          <span className="text-gray-500 text-sm">/{t('catering.pax')}</span>
        </div>

        {/* Min Order */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span>{t('catering.minimumOrder')}: <strong>{pkg.minimum_order_pax} {t('catering.pax')}</strong></span>
        </div>

        {/* Perks */}
        {perks.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('catering.perks')}</p>
            <div className="flex flex-wrap gap-2">
              {perks.slice(0, 4).map((perk, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full"
                >
                  <span>{PERK_ICONS[perk.icon] || '✓'}</span>
                  {getLocalizedField(perk, 'name', lang)}
                </span>
              ))}
              {perks.length > 4 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{perks.length - 4}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Menu Preview */}
        {menuItems.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('catering.menuIncludes')}</p>
            <ul className="text-sm text-gray-600 space-y-1">
              {menuItems.slice(0, 5).map((item, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  {getLocalizedField(item, 'name', lang)}
                </li>
              ))}
              {menuItems.length > 5 && (
                <li className="text-primary-600 text-sm">+{menuItems.length - 5} more items</li>
              )}
            </ul>
          </div>
        )}

        {/* Lead time */}
        {pkg.lead_time_days && (
          <p className="text-xs text-gray-500 mb-4">
            📅 {t('catering.leadTime', { days: pkg.lead_time_days })}
          </p>
        )}

        {/* Spacer */}
        <div className="flex-1"></div>

        {/* CTA Button */}
        <button
          onClick={() => onInquire(pkg)}
          className="w-full py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          {t('catering.inquireNow')}
        </button>
      </div>
    </div>
  );
}

// Inquiry Form Modal
function InquiryModal({ pkg, onClose, onSuccess, t, lang }) {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (data) => {
    setSubmitting(true);
    setError('');

    try {
      const { error: insertError } = await supabase
        .from('catering_inquiries')
        .insert({
          package_id: pkg?.id || null,
          customer_name: data.name,
          customer_email: data.email,
          customer_phone: data.phone,
          company_name: data.company || null,
          event_type: data.eventType,
          event_date: data.eventDate,
          event_time: data.eventTime || null,
          event_location: data.location,
          event_address: data.address || null,
          estimated_pax: parseInt(data.pax),
          budget_range: data.budget || null,
          dietary_requirements: data.dietary ? [data.dietary] : [],
          special_requests: data.requests || null,
          status: 'new',
        });

      if (insertError) throw insertError;
      onSuccess();
    } catch (err) {
      console.error('Submit error:', err);
      setError(t('catering.form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const eventTypes = ['wedding', 'corporate', 'birthday', 'gathering', 'other'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('catering.form.title')}</h2>
            <p className="text-sm text-gray-600">{t('catering.form.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Selected Package */}
          {pkg && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="text-sm text-primary-700">
                <span className="font-medium">{t('catering.form.selectedPackage')}:</span>{' '}
                {getLocalizedField(pkg, 'name', lang)}
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              {error}
            </div>
          )}

          {/* Customer Info */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 border-b pb-2">{t('catering.form.contactInfo')}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">{t('catering.form.name')} *</label>
                <input
                  type="text"
                  {...register('name', { required: true })}
                  className={`input-field ${errors.name ? 'border-red-500' : ''}`}
                />
              </div>

              <div>
                <label className="label">{t('catering.form.phone')} *</label>
                <input
                  type="tel"
                  {...register('phone', { required: true })}
                  className={`input-field ${errors.phone ? 'border-red-500' : ''}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">{t('catering.form.email')} *</label>
                <input
                  type="email"
                  {...register('email', { required: true })}
                  className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                />
              </div>

              <div>
                <label className="label">{t('catering.form.company')}</label>
                <input
                  type="text"
                  {...register('company')}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 border-b pb-2">{t('catering.form.eventDetails')}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">{t('catering.form.eventType')} *</label>
                <select
                  {...register('eventType', { required: true })}
                  className={`input-field ${errors.eventType ? 'border-red-500' : ''}`}
                >
                  <option value="">Select...</option>
                  {eventTypes.map((type) => (
                    <option key={type} value={type}>
                      {t(`catering.form.eventTypes.${type}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">{t('catering.form.pax')} *</label>
                <input
                  type="number"
                  min="1"
                  {...register('pax', { required: true, min: 1 })}
                  className={`input-field ${errors.pax ? 'border-red-500' : ''}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">{t('catering.form.eventDate')} *</label>
                <input
                  type="date"
                  {...register('eventDate', { required: true })}
                  min={new Date().toISOString().split('T')[0]}
                  className={`input-field ${errors.eventDate ? 'border-red-500' : ''}`}
                />
              </div>

              <div>
                <label className="label">{t('catering.form.eventTime')}</label>
                <input
                  type="time"
                  {...register('eventTime')}
                  className="input-field"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">{t('catering.form.location')} *</label>
                <input
                  type="text"
                  placeholder="e.g., Jakarta Selatan"
                  {...register('location', { required: true })}
                  className={`input-field ${errors.location ? 'border-red-500' : ''}`}
                />
              </div>

              <div>
                <label className="label">{t('catering.form.budget')}</label>
                <input
                  type="text"
                  {...register('budget')}
                  className="input-field"
                  placeholder={t('catering.form.budgetPlaceholder')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('catering.form.budgetHint')}</p>
              </div>
            </div>

            <div>
              <label className="label">{t('catering.form.address')}</label>
              <textarea
                {...register('address')}
                rows={2}
                className="input-field"
                placeholder="Full venue address..."
              />
            </div>
          </div>

          {/* Halal & Thoyyiban Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">☪️</span>
              <div>
                <h4 className="font-semibold text-green-800">{t('catering.form.halalTitle')}</h4>
                <p className="text-sm text-green-700 mt-1">{t('catering.form.halalDescription')}</p>
              </div>
            </div>
          </div>

          {/* Dietary Notes */}
          <div>
            <label className="label">{t('catering.form.dietary')}</label>
            <p className="text-xs text-gray-500 mb-2">{t('catering.form.dietaryHint')}</p>
            <textarea
              {...register('dietary')}
              rows={2}
              className="input-field"
              placeholder={t('catering.form.dietaryPlaceholder')}
            />
          </div>

          {/* Special Requests */}
          <div>
            <label className="label">{t('catering.form.requests')}</label>
            <textarea
              {...register('requests')}
              rows={3}
              className="input-field"
              placeholder="Any special requirements or questions..."
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? t('catering.form.submitting') : t('catering.form.submit')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Success Modal
function SuccessModal({ onClose, t }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('catering.form.thankYou')}</h2>
        <p className="text-gray-600 mb-6">{t('catering.form.success')}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

// Main Catering Page
export default function Catering() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const headerRef = useRef(null);
  const sectionTitleRef = useRef(null);
  const whyChooseRef = useRef(null);

  // Animate header on mount
  useEffect(() => {
    if (!loading && headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.2, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Animate section title
  useEffect(() => {
    if (!loading && sectionTitleRef.current) {
      gsap.fromTo(
        sectionTitleRef.current,
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.6, delay: 0.4, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Animate why choose section
  useEffect(() => {
    if (!loading && whyChooseRef.current) {
      gsap.set(whyChooseRef.current, { opacity: 0, y: 40 });

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              gsap.to(whyChooseRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power3.out',
              });
              observer.unobserve(whyChooseRef.current);
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(whyChooseRef.current);
      return () => observer.disconnect();
    }
  }, [loading]);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('catering_packages')
        .select('*')
        .eq('is_active', true)
        .order('is_featured', { ascending: false })
        .order('sort_order');

      if (fetchError) throw fetchError;
      setPackages(data || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInquire = (pkg) => {
    setSelectedPackage(pkg);
    setShowInquiryForm(true);
  };

  const handleInquireGeneral = () => {
    setSelectedPackage(null);
    setShowInquiryForm(true);
  };

  const handleSuccess = () => {
    setShowInquiryForm(false);
    setShowSuccess(true);
    setSelectedPackage(null);
  };

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div ref={headerRef} className="text-center mb-12">
          {/* Catering Logo */}
          <img
            src={cateringLogo}
            alt="Ampera Catering"
            className="h-32 md:h-40 w-auto mx-auto mb-6"
          />
          <SectionHeader title={t('catering.title')} subtitle={t('catering.subtitle')} className="mb-8" />
          <Button variant="primary" onClick={handleInquireGeneral}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {t('catering.inquireNow')}
          </Button>
        </div>

        {/* Packages Section */}
        <div className="mb-8">
          <h2 ref={sectionTitleRef} className="text-2xl font-bold text-gray-900 mb-6">{t('catering.packages')}</h2>
        </div>

        <DataState
          loading={loading}
          error={error}
          empty={packages.length === 0}
          onRetry={fetchPackages}
          skeleton={<SkeletonGrid count={6} className={CATERING_GRID} />}
          emptyProps={{
            icon: '📋',
            message: t('catering.noPackages'),
            action: (
              <Button variant="primary" onClick={handleInquireGeneral}>
                {t('catering.inquireNow')}
              </Button>
            ),
          }}
        >
          <div className={CATERING_GRID}>
            {packages.map((pkg, index) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                lang={lang}
                onInquire={handleInquire}
                t={t}
                index={index}
              />
            ))}
          </div>
        </DataState>

        {/* Why Choose Us Section */}
        <div ref={whyChooseRef} className="mt-16 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t('catering.whyChoose')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <AnimatedFeatureCard
              index={0}
              icon="☪️"
              title={t('catering.features.halal.title')}
              description={t('catering.features.halal.description')}
            />
            <AnimatedFeatureCard
              index={1}
              icon="🍛"
              title={t('catering.features.authentic.title')}
              description={t('catering.features.authentic.description')}
            />
            <AnimatedFeatureCard
              index={2}
              icon="✅"
              title={t('catering.features.quality.title')}
              description={t('catering.features.quality.description')}
            />
            <AnimatedFeatureCard
              index={3}
              icon="🎯"
              title={t('catering.features.flexible.title')}
              description={t('catering.features.flexible.description')}
            />
          </div>
        </div>
      </div>

      {/* Inquiry Form Modal */}
      {showInquiryForm && (
        <InquiryModal
          pkg={selectedPackage}
          onClose={() => setShowInquiryForm(false)}
          onSuccess={handleSuccess}
          t={t}
          lang={lang}
        />
      )}

      {/* Success Modal */}
      {showSuccess && (
        <SuccessModal
          onClose={() => setShowSuccess(false)}
          t={t}
        />
      )}
    </div>
  );
}
